import get from 'lodash/get';

/** @typedef {'Active'|'PendingAgreement'|'PendingActivation'|'Inactive'|'PendingRenewal'|'Deactivated'|'Cancelled'} ClientStatus */

export const CLIENT_STATUS_CONFIG = {
  Active: { label: 'Active', className: 'bg-success-bg text-success', usable: true },
  // PendingActivation label is billing-path specific — see statusBadgeConfig (R-7513).
  PendingActivation: { label: 'Awaiting activation', className: 'bg-warning-bg text-warning', usable: false },
  PendingAgreement: { label: 'Awaiting agreement', className: 'bg-warning-bg text-warning', usable: false },
  Inactive: { label: 'Paused', className: 'bg-muted text-muted-foreground', usable: false },
  PendingRenewal: { label: 'Renewal due', className: 'bg-warning-bg text-warning', usable: false },
  Deactivated: { label: 'Ended', className: 'bg-muted text-muted-foreground', usable: false },
  Cancelled: { label: 'Cancelled', className: 'bg-danger-bg text-danger', usable: false },
};

const PENDING_ACTIVATION_WARNING = { className: 'bg-warning-bg text-warning', usable: false };

const LEGACY_STATUS_MAP = {
  active: 'Active',
  pending_agreement: 'PendingAgreement',
  pending_activation: 'PendingActivation',
  inactive: 'Inactive',
  pending_renewal: 'PendingRenewal',
  deactivated: 'Deactivated',
  cancelled: 'Cancelled',
};

/**
 * Normalise org lifecycle status (prefer PascalCase `status`, fall back to legacy `organisationStatus`).
 * @param {object} org
 * @returns {string|undefined}
 */
export const resolveClientStatus = (org) => {
  if (!org) return undefined;
  if (org.status) return org.status;
  const legacy = org.organisationStatus;
  if (legacy && LEGACY_STATUS_MAP[legacy]) return LEGACY_STATUS_MAP[legacy];
  if (org.active === false) return 'Inactive';
  if (org.active === true) return 'Active';
  return undefined;
};

/**
 * Mirror backend isClientUsable() for consultant client orgs.
 * @param {object} org
 * @param {{ billingStatus?: string }} [options]
 * @returns {boolean}
 */
export const isClientUsable = (org, options = {}) => {
  if (!org) return false;

  const status = resolveClientStatus(org);
  if (status !== 'Active') return false;
  if (org.reportGeneration === false) return false;

  const relationship = org.billingRelationship;

  if (relationship === 'direct_billed') {
    return org.hasSubscriptionActive === true;
  }

  if (relationship === 'consultant_billed') {
    const billingStatus = options.billingStatus ?? org.billingStatus;
    if (billingStatus && billingStatus !== 'current') return false;
    return true;
  }

  return true;
};

/**
 * Human-readable reason when a client cannot generate reports.
 * @param {object} org
 * @returns {string|null}
 */
export const getClientUnusableReason = (org) => {
  if (!org) return 'Client organisation not found.';

  const status = resolveClientStatus(org);

  if (status && status !== 'Active') {
    const label = statusBadgeConfig(org)?.label;
    return label
      ? `This client is ${label.toLowerCase()} and cannot generate reports yet.`
      : 'This client account is not active for report generation.';
  }

  if (org.reportGeneration === false) {
    return 'Report generation is disabled for this client.';
  }

  if (org.billingRelationship === 'direct_billed' && org.hasSubscriptionActive !== true) {
    return 'The client has not activated their subscription yet. Facilities, usage, and reports stay locked until they accept the order form and complete payment.';
  }

  if (org.billingRelationship === 'consultant_billed' && org.billingStatus && org.billingStatus !== 'current') {
    return 'This client billing is not current.';
  }

  return null;
};

/**
 * @param {string|undefined} relationship
 * @returns {string}
 */
export const billingRelationshipLabel = (relationship) => {
  if (relationship === 'direct_billed') return 'Client subscription';
  if (relationship === 'consultant_billed') return 'Billed to you';
  return 'Legacy consultant';
};

/**
 * Portfolio / detail status badge. PendingActivation splits by billing path (R-7513):
 * consultant-billed → "Awaiting activation"; direct-billed → "Awaiting client signature".
 * @param {object} org
 * @returns {{ label: string, className: string, usable: boolean }}
 */
export const statusBadgeConfig = (org) => {
  const status = resolveClientStatus(org) || 'Active';
  if (status === 'PendingActivation') {
    if (org?.billingRelationship === 'direct_billed') {
      return { label: 'Awaiting client signature', ...PENDING_ACTIVATION_WARNING };
    }
    return { label: 'Awaiting activation', ...PENDING_ACTIVATION_WARNING };
  }
  return CLIENT_STATUS_CONFIG[status] || { label: status, className: 'bg-muted text-muted-foreground', usable: false };
};

export const isConsultantClientOrg = (org, consultantParentId) => {
  const consultantId = get(org, 'consultantId');
  if (!consultantId || consultantId === 'NONE') return false;
  if (org.id === consultantId) return false; // firm itself
  if (consultantParentId && org.id === consultantParentId) return false;
  // When firm id is known, only that consultant’s clients — never every consultant client on the platform
  if (consultantParentId) return String(consultantId) === String(consultantParentId);
  return true;
};

/**
 * Copy shown when a consultant end-client workspace is locked (no usable subscription).
 * Covers both billing paths so consultants are not left guessing why Facilities/Usage are disabled.
 * @param {object} org
 * @returns {string|null}
 */
export const getConsultantClientAccessLockMessage = (org) => {
  if (!isConsultantClientOrg(org)) return null;
  if (org.hasSubscriptionActive === true) return null;

  if (org.billingRelationship === 'consultant_billed') {
    return 'This client organisation is not activated yet. Open Client Portfolio and choose Activate before adding facilities or usage data.';
  }

  return 'This client pays via their own subscription. Facilities and usage stay locked until they accept the order form and complete payment. Resend the invitation from Client Portfolio if they have not received it.';
};

export const filterConsultantClients = (orgs, consultantParentId) =>
  (orgs || []).filter((org) => isConsultantClientOrg(org, consultantParentId));

export const CONSULTANT_CLIENT_ERROR_MESSAGES = {
  not_consultant_admin: 'You do not have consultant admin access.',
  invalid_billing_relationship: 'Please choose how this client will be billed.',
  invalid_plan: 'Select a plan type and pricing for this client.',
  consultant_price_not_accepted: 'Select an accepted Plan and FTE band.',
  consultant_master_contract_missing:
    'Your consultant agreement is not complete. Contact Compare Your Footprint to finish setup before adding clients.',
  agreements_flow_disabled: 'Direct client billing is not available yet. Use consultant-billed for now.',
  price_not_in_allowlist: 'The selected plan is not available for direct client billing.',
  client_already_active: 'This client is already active — resend and void are not available.',
  not_consultant_billed: 'This action only applies to consultant-billed clients.',
  schedule_not_found: 'No billing schedule found for this client.',
  schedule_already_exists: 'This client already has a billing schedule.',
  invalid_organisation_id: 'Select a client organisation to onboard.',
  not_consultant_client: 'This organisation is not a consultant end client.',
  not_authorised: 'You do not have permission for this action.',
  forbidden: 'You do not have access to this client.',
  not_found: 'Client or contract not found.',
  cannot_resend_in_status: 'Cannot resend the invitation in the current client status.',
};

/**
 * @param {string|object} error
 * @returns {string}
 */
export const friendlyConsultantClientError = (error) => {
  const code = typeof error === 'string' ? error : error?.error || error?.code;
  if (code && CONSULTANT_CLIENT_ERROR_MESSAGES[code]) return CONSULTANT_CLIENT_ERROR_MESSAGES[code];
  if (typeof error === 'object' && error?.message) return error.message;
  return 'Something went wrong. Please try again.';
};

const PENDING_CONTRACTS_KEY = 'consultantPendingContracts';

export const loadPendingContractsFromSession = () => {
  try {
    const raw = sessionStorage.getItem(PENDING_CONTRACTS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

export const savePendingContractToSession = (orgId, contractId) => {
  if (!orgId || !contractId) return;
  const map = loadPendingContractsFromSession();
  map[orgId] = contractId;
  sessionStorage.setItem(PENDING_CONTRACTS_KEY, JSON.stringify(map));
};

export const removePendingContractFromSession = (orgId) => {
  if (!orgId) return;
  const map = loadPendingContractsFromSession();
  delete map[orgId];
  sessionStorage.setItem(PENDING_CONTRACTS_KEY, JSON.stringify(map));
};

/**
 * @param {string|undefined} planType
 * @param {object} [client]
 * @returns {string}
 */
export const planTypeLabel = (planType, client) => {
  if (planType === 'catalog_plan') return 'Catalog plan';
  if (planType === 'client_subscription') return 'Client subscription';
  if (planType === 'per_report') return 'Per report';
  if (!planType && client && !client.billingRelationship) return 'Per report (legacy)';
  if (!planType && client?.billingRelationship === 'consultant_billed') return 'Per report';
  return planType || '—';
};

const UUID_LIKE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Organisation `currency` is often a currencies-table UUID, not `£`.
 * @param {*} value
 * @returns {string}
 */
export const resolvePriceCurrencySymbol = (value) => {
  if (value == null || value === '') return '£';
  const s = String(value).trim();
  if (UUID_LIKE.test(s) || s.length > 4) return '£';
  return s;
};

/**
 * Format a per-report fee. Legacy rows store a pricing-band UUID in `perReportFeeVal`.
 * @param {*} val
 * @param {string} [currencySymbol='£']
 * @param {string} [suffix=' / report']
 * @returns {string|null}
 */
export const formatPerReportFeeLabel = (val, currencySymbol = '£', suffix = ' / report') => {
  if (val == null || val === '') return null;
  const raw = String(val).trim();
  if (UUID_LIKE.test(raw) || Number.isNaN(Number(raw))) return 'Tiered per report';
  const n = Number(raw);
  if (!Number.isFinite(n)) return 'Tiered per report';
  const shown = Number.isInteger(n) ? String(n) : n.toFixed(2);
  const symbol = resolvePriceCurrencySymbol(currencySymbol);
  return `${symbol}${shown}${suffix}`;
};

/**
 * Resolve display price for a consultant client row.
 * @param {object} client
 * @param {object|null} schedule
 * @param {number|null} masterPerReportFeeVal
 * @param {string} [currencySymbol='£']
 * @returns {string}
 */
export const resolveClientPriceLabel = (client, schedule, masterPerReportFeeVal, currencySymbol = '£') => {
  const symbol = resolvePriceCurrencySymbol(currencySymbol || client?.currency);
  if (client?.billingRelationship === 'direct_billed' && !schedule) return 'Paid by client';
  if (schedule?.planType === 'catalog_plan' && schedule.netLicensePrice != null) {
    const standard = Number(schedule.standardLicensePrice);
    const net = Number(schedule.netLicensePrice);
    const netLabel = `${symbol}${Number.isInteger(net) ? net : net.toFixed(2)} / year`;
    if (Number.isFinite(standard) && standard !== net) {
      return `${symbol}${standard} → ${netLabel}`;
    }
    return netLabel;
  }
  if (schedule?.planType === 'per_report' && schedule.perReportFeeVal != null) {
    return formatPerReportFeeLabel(schedule.perReportFeeVal, symbol) || '—';
  }
  if (schedule?.unitAmount != null) {
    const n = Number(schedule.unitAmount);
    if (Number.isFinite(n)) {
      const shown = Number.isInteger(n) ? String(n) : n.toFixed(2);
      return `${symbol}${shown} / period`;
    }
  }
  if (!schedule && resolveClientStatus(client) === 'Active' && masterPerReportFeeVal != null) {
    const fee = formatPerReportFeeLabel(masterPerReportFeeVal, symbol);
    if (!fee) return '—';
    if (fee === 'Tiered per report') return 'Tiered per report (legacy master fee)';
    return `${fee} (legacy master fee)`;
  }
  return '—';
};

/**
 * Calculate the accepted consultant price for one Plan × FTE cell.
 * Mirrors the backend order: programme discount, then 5% for 36 months.
 *
 * @param {object} catalog
 * @param {object} programme
 * @param {string} planId
 * @param {string} fteBand
 * @param {number} termMonths
 * @returns {{ standard: number, programmeDiscountPercent: number, commitmentDiscountPercent: number, net: number, savings: number, monthly: number }|null}
 */
export const resolveConsultantCatalogPrice = (
  catalog,
  programme,
  planId,
  fteBand,
  termMonths = 12,
) => {
  const standard = Number(catalog?.licensePrices?.[planId]?.[fteBand]);
  if (!Number.isFinite(standard)) return null;
  const programmeDiscountPercent = Number(programme?.discountPercent) || 0;
  const commitmentDiscountPercent = Number(termMonths) === 36 ? 5 : 0;
  const afterProgramme = Math.round(standard * (1 - programmeDiscountPercent / 100) * 100) / 100;
  const net = Math.round(afterProgramme * (1 - commitmentDiscountPercent / 100) * 100) / 100;
  return {
    standard,
    programmeDiscountPercent,
    commitmentDiscountPercent,
    net,
    savings: Math.round((standard - net) * 100) / 100,
    monthly: Math.round((net / 12) * 100) / 100,
  };
};

/**
 * Resolve commitment end display for portfolio / detail.
 * @param {object} client
 * @param {object|null} schedule
 * @returns {string}
 */
export const resolveCommitmentEndLabel = (client, schedule) => {
  if (schedule?.commitmentEndDate) return schedule.commitmentEndDate;
  if (client?.subscriptionEndDate) return client.subscriptionEndDate;
  return 'Set by CYF admin';
};

/**
 * Clients under consultant missing a billing schedule row.
 * @param {object[]} clients
 * @param {string} consultantParentId
 * @returns {object[]}
 */
export const clientsMissingSchedule = (clients, consultantParentId) =>
  filterConsultantClients(clients, consultantParentId).filter(
    (c) => c.billingRelationship !== 'direct_billed' && !c.consultantClientScheduleId,
  );
